import { useState } from 'react';
import { Mail, MessageSquare, CheckCircle2, Loader2 } from 'lucide-react';
import { TopHeader } from '../../components';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../api/client';
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from '../../components/inputs/Dropdown';

const SUBJECTS = [
  'General question',
  'Technical issue',
  'Billing & plans',
  'Feature request',
  'Data access or deletion request',
  'Other',
];

export function ContactPage() {
  const { user } = useAuth();

  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.subject || !form.message.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    setIsSubmitting(true);
    try {
      await apiClient.post('/api/v1/contact', { ...form, source: 'app' });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = 'w-full px-3 py-2.5 rounded-lg border border-border bg-white text-text-primary text-sm placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors';
  const labelClass = 'block text-sm font-medium text-text-primary mb-1.5';

  return (
    <div className="min-h-screen flex flex-col">
      <TopHeader title="Support" />

      <div className="flex-1 px-4 sm:px-8 py-6 max-w-2xl">
        {submitted ? (
          <div className="bg-background-card rounded-xl border border-border p-8 flex flex-col items-center text-center gap-4">
            <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center">
              <CheckCircle2 size={28} className="text-success" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary mb-1">Message sent!</h2>
              <p className="text-text-secondary text-sm">
                Thanks for getting in touch. We'll get back to you at <strong className="text-text-primary">{form.email}</strong> within one business day.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Intro */}
            <div className="bg-background-card rounded-xl border border-border p-5 flex gap-4 items-start">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <MessageSquare size={18} className="text-primary" />
              </div>
              <div>
                <h2 className="font-semibold text-text-primary mb-0.5">Get in touch</h2>
                <p className="text-sm text-text-secondary">
                  Have a question, feedback, or need help? We're here to help.
                </p>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-background-card rounded-xl border border-border p-5 sm:p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Name</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    className={inputClass}
                    required
                  />
                </div>
                <div>
                  <label className={labelClass}>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className={inputClass}
                    required
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>Subject</label>
                <Dropdown
                  value={form.subject}
                  onSelect={(value) => {
                    setForm((prev) => ({ ...prev, subject: value }));
                    setError('');
                  }}
                >
                  <DropdownTrigger placeholder="Select a topic…">
                    {form.subject || undefined}
                  </DropdownTrigger>
                  <DropdownMenu>
                    {SUBJECTS.map((s) => (
                      <DropdownItem key={s} value={s}>{s}</DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              </div>

              <div>
                <label className={labelClass}>Message</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us how we can help…"
                  rows={5}
                  className={`${inputClass} resize-none`}
                  required
                />
              </div>

              {error && (
                <p className="text-sm text-error">{error}</p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-text-inverse rounded-lg font-medium text-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Mail size={16} />
                    Send message
                  </>
                )}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
